import React from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

interface ApiSelectOption {
  value: string;
  label: string;
}

interface UseQueryResult<T> {
  data?: T[];
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
}

interface ApiSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  error?: string;
  helperText?: string;
  placeholder?: string;
  useQueryHook: () => UseQueryResult<unknown>;
  mapDataToOptions: (data: unknown[]) => ApiSelectOption[];
  emptyMessage?: string;
  errorMessage?: string;
  className?: string;
  allowEmpty?: boolean;
  emptyOptionLabel?: string;
}

const ApiSelect = React.forwardRef<HTMLSelectElement, ApiSelectProps>(
  ({ 
    label, 
    error, 
    helperText, 
    placeholder, 
    useQueryHook,
    mapDataToOptions,
    emptyMessage = 'No hay opciones disponibles',
    errorMessage = 'Error al cargar opciones',
    className = '', 
    allowEmpty = false,
    emptyOptionLabel,
    ...props 
  }, ref) => {
    const { data, isLoading, isError } = useQueryHook();

    const options: ApiSelectOption[] = data ? mapDataToOptions(data) : [];

    return (
      <div className="space-y-1">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            className={`
              w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
              disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
              appearance-none bg-white pr-10
              ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
              ${isLoading ? 'cursor-wait' : ''}
              ${className}
            `}
            disabled={isLoading || props.disabled}
            {...props}
          >
            {!allowEmpty && placeholder && (
              <option value="" disabled>
                {isLoading ? 'Cargando...' : placeholder}
              </option>
            )}
            
            {isError && (
              <option value="" disabled>
                {errorMessage}
              </option>
            )}
            
            {allowEmpty && !isLoading && !isError && (
              <option value="">
                {placeholder ?? emptyOptionLabel ?? ''}
              </option>
            )}

            {!isLoading && !isError && options.length === 0 && (
              <option value="" disabled>
                {emptyMessage}
              </option>
            )}
            
            {!isLoading && !isError && options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            {isLoading ? (
              <Loader2 size={16} className="animate-spin text-gray-400" />
            ) : (
              <ChevronDown size={16} className="text-gray-400" />
            )}
          </div>
        </div>
        
        {error && (
          <p className="text-sm text-red-600">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

ApiSelect.displayName = 'ApiSelect';

export default ApiSelect;
